import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Project {
  id: string;
  project_name: string;
  description?: string;
  technologies?: string[];
  image_url?: string;
  github_url?: string;
  live_demo_url?: string;
}

interface ProjectDetailModalProps {
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
}

export const ProjectDetailModal = ({ project, isOpen, onClose }: ProjectDetailModalProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [images, setImages] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchImages = async () => {
      if (!project?.id) return;

      setLoading(true);

      try {
        const { data, error } = await supabase
          .from("project_images")
          .select("image_url")
          .eq("project_id", project.id)
          .order("display_order");

        if (error) throw error;

        if (!isMounted) return;

        const imageUrls = data?.map(img => img.image_url) || [];

        if (imageUrls.length === 0 && project.image_url) {
          setImages([project.image_url]);
        } else {
          setImages(imageUrls);
        }
      } catch (error) {
        console.error("Error fetching project images:", error);
        if (!isMounted) return;
        if (project.image_url) {
          setImages([project.image_url]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (isOpen && project) {
      setCurrentImageIndex(0);
      setImages([]);
      fetchImages();
    }

    return () => {
      isMounted = false;
    };
  }, [project, isOpen]);

  const nextImage = () => {
    if (images.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }
  };

  const prevImage = () => {
    if (images.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            {project?.project_name || "Project Details"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Details about {project?.project_name || "this"} project
          </DialogDescription>
        </DialogHeader>

        {project ? (
          <div className="space-y-6">
            {/* Image Gallery */}
            {loading ? (
              <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
                <Icon name="progress_activity" size={32} className="animate-spin text-muted-foreground" />
              </div>
            ) : images.length > 0 ? (
              <div className="relative">
                <div className="aspect-video bg-muted rounded-lg overflow-hidden">
                  <img
                    src={images[currentImageIndex]}
                    alt={`${project.project_name} - Image ${currentImageIndex + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>

                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-background/80 hover:bg-background p-2 rounded-full shadow-lg transition-colors"
                      aria-label="Previous image"
                      type="button"
                    >
                      <Icon name="chevron_left" size={24} />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-background/80 hover:bg-background p-2 rounded-full shadow-lg transition-colors"
                      aria-label="Next image"
                      type="button"
                    >
                      <Icon name="chevron_right" size={24} />
                    </button>

                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                      {images.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentImageIndex(idx)}
                          className={`w-2 h-2 rounded-full transition-colors ${idx === currentImageIndex
                              ? "bg-primary"
                              : "bg-background/60 hover:bg-background/80"
                            }`}
                          aria-label={`Go to image ${idx + 1}`}
                          type="button"
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
                <Icon name="rocket_launch" size={64} className="text-muted-foreground/40" />
              </div>
            )}

            {/* Project Links */}
            {(project.github_url || project.live_demo_url) && (
              <div className="flex flex-wrap gap-3">
                {project.github_url && (
                  <Button asChild variant="outline" className="rounded-full">
                    <a href={project.github_url} target="_blank" rel="noopener noreferrer">
                      <Icon name="code" size={18} className="mr-2" />
                      View Source Code
                    </a>
                  </Button>
                )}
                {project.live_demo_url && (
                  <Button asChild className="rounded-full">
                    <a href={project.live_demo_url} target="_blank" rel="noopener noreferrer">
                      <Icon name="open_in_new" size={18} className="mr-2" />
                      Live Demo
                    </a>
                  </Button>
                )}
              </div>
            )}

            {/* Description */}
            {project.description && (
              <div>
                <h3 className="text-lg font-semibold mb-2">About This Project</h3>
                <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                  {project.description}
                </p>
              </div>
            )}

            {/* Technologies */}
            {project.technologies && project.technologies.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3">Technologies Used</h3>
                <div className="flex flex-wrap gap-2">
                  {project.technologies.map((tech, idx) => (
                    <Badge
                      key={idx}
                      className="px-4 py-2 text-sm rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors border-0 font-medium"
                    >
                      {tech}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="py-8 text-center text-muted-foreground">
            Loading project details...
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
